
import { useState } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { Navigate } from 'react-router-dom';
import { Box, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';
import logo from '../assets/logo.png';
import { usePageTitle } from '../lib/usePageTitle';

export function Login() {
    const { signInWithGoogle, session } = useAuth();
    usePageTitle('Sign In');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [fullName, setFullName] = useState('');
    const [companyName, setCompanyName] = useState('');

    if (session) {
        return <Navigate to="/" replace />;
    }

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setMessage(null);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            setIsLoading(false);
            return;
        }

        try {
            if (isForgotPassword) {
                const { error } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: window.location.origin + '/reset-password',
                });
                if (error) throw error;
                setMessage("Check your email for the password reset link!");
            } else if (isSignUp) {
                if (password !== confirmPassword) {
                    throw new Error("Passwords do not match");
                }
                const { data, error } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                        data: {
                            full_name: fullName,
                            company_name: companyName,
                        }
                    }
                });
                if (error) throw error;

                if (data?.user?.identities && data.user.identities.length === 0) {
                    throw new Error("already registered");
                }

                setMessage("Check your email for the confirmation link!");
            } else {
                const { error } = await supabase.auth.signInWithPassword({
                    email,
                    password,
                });
                if (error) throw error;
            }
        } catch (err: any) {
            const errorMsg = err.message || '';
            if (isSignUp && (errorMsg.toLowerCase().includes('already registered') || errorMsg.toLowerCase().includes('already exists'))) {
                setError('This email is already in use, please login or reset password?');
            } else {
                setError(err.message || 'An error occurred during authentication');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            {/* Left Side - Visual */}
            <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1631541909061-71e349d1f241?q=80&w=1974&auto=format&fit=crop')] opacity-20 bg-cover bg-center" />
                <div className="relative z-10 p-12 text-white max-w-lg">
                    <div className="mb-8 drop-shadow-[0_0_40px_rgba(255,255,255,0.2)]">
                        <img src={logo} alt="Print My Parts" className="h-16 w-auto" />
                    </div>
                    <h1 className="text-5xl font-bold mb-6 tracking-tight">Industrial 3D Printing, Simplified.</h1>
                    <p className="text-xl text-slate-400 font-light leading-relaxed">
                        Instant quotes, engineering-grade materials, and rapid turnaround times. Join over 500 engineers building the future.
                    </p>
                </div>
            </div>

            {/* Right Side - Login */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center lg:text-left">
                        <h2 className="text-3xl font-bold text-slate-900">
                            {isForgotPassword ? 'Reset Password' : (isSignUp ? 'Create an Account' : 'Welcome Back')}
                        </h2>
                        <p className="text-slate-500 mt-2">
                            {isForgotPassword
                                ? 'Enter your email to receive valid wait time link'
                                : (isSignUp ? 'Enter your details to get started.' : 'Sign in to manage your quotes and orders.')}
                        </p>
                    </div>

                    <div className="space-y-4">
                        {!isForgotPassword && (
                            <>
                                <button
                                    onClick={() => signInWithGoogle()}
                                    className="w-full bg-white text-slate-700 font-bold border border-slate-200 p-4 rounded-xl flex items-center justify-center gap-3 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
                                >
                                    <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-6 h-6" alt="Google" />
                                    Sign in with Google
                                </button>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-slate-200" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-slate-50 px-2 text-slate-400">Or continue with email</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {error && (
                            <div className="p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2 text-sm">
                                <AlertCircle size={16} />
                                {error}
                            </div>
                        )}

                        {message && (
                            <div className="p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2 text-sm">
                                <AlertCircle size={16} />
                                {message}
                            </div>
                        )}

                        <form className="space-y-4" onSubmit={handleEmailAuth}>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-500 uppercase">Email</label>
                                <input
                                    required
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Email"
                                    maxLength={255}
                                    className="w-full p-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                />
                            </div>

                            {isSignUp && (
                                <>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase">Full Name</label>
                                        <input
                                            required
                                            type="text"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            placeholder="John Doe"
                                            maxLength={100}
                                            className="w-full p-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-500 uppercase">
                                            Company Name <span className="text-slate-400 normal-case">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            placeholder="Your Company Inc."
                                            maxLength={100}
                                            className="w-full p-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                        />
                                    </div>
                                </>
                            )}

                            {!isForgotPassword && (
                                <>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Password</label>
                                            {!isSignUp && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsForgotPassword(true);
                                                        setError(null);
                                                        setMessage(null);
                                                    }}
                                                    className="text-xs text-brand hover:underline"
                                                >
                                                    Forgot Password?
                                                </button>
                                            )}
                                        </div>
                                        <div className="relative">
                                            <input
                                                required
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                placeholder="Password"
                                                maxLength={128}
                                                className="w-full p-3 pr-12 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                            >
                                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    {isSignUp && (
                                        <div className="space-y-2 mt-4">
                                            <label className="text-xs font-bold text-slate-500 uppercase">Confirm Password</label>
                                            <div className="relative">
                                                <input
                                                    required
                                                    type={showPassword ? "text" : "password"}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    placeholder="Confirm Password"
                                                    maxLength={128}
                                                    className="w-full p-3 pr-12 rounded-lg border border-slate-200 focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                                                >
                                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            <button
                                disabled={isLoading}
                                className="w-full bg-slate-900 text-white font-bold p-4 rounded-xl hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                            >
                                {isLoading ? <Loader2 className="animate-spin" /> : (isForgotPassword ? 'Send Reset Link' : (isSignUp ? 'Create Account' : 'Sign In'))}
                            </button>
                        </form>

                        <div className="text-center space-y-2">
                            {isForgotPassword ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsForgotPassword(false);
                                        setError(null);
                                        setMessage(null);
                                    }}
                                    className="text-sm text-slate-500 hover:text-brand font-medium"
                                >
                                    Back to Sign In
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsSignUp(!isSignUp);
                                        setConfirmPassword('');
                                        setError(null);
                                        setMessage(null);
                                    }}
                                    className="text-sm text-slate-500 hover:text-brand font-medium"
                                >
                                    {isSignUp ? (
                                        <>Already have an account? <span className="font-bold">Sign In</span></>
                                    ) : (
                                        <>Don't have an account? <span className="font-bold">Sign Up</span></>
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-slate-200" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-slate-50 px-2 text-slate-400">Or</span>
                            </div>
                        </div>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => window.location.href = '/new'}
                                className="text-sm text-brand hover:text-brand/80 font-bold flex items-center justify-center gap-2 mx-auto"
                            >
                                Continue as Guest
                                <Box size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
